/**
 * A whole record's names, from its cells and its schema's rules.
 *
 * The grid previews these and stores what it previewed; the taxonomy agent's
 * tools write records on the server. Both call `assembleRecord`, so a row the
 * agent wrote carries exactly the name the grid would have given it.
 */
import { assembleOutput, } from './assemble';
/**
 * `groups` is optional on the wire and rules are copied verbatim into every new
 * version, so a branch authored outside the UI keeps that shape forever. Every
 * reader goes through here rather than dereferencing an array that is not there.
 */
function normalizeGroup(group) {
    return {
        combinator: group?.combinator ?? 'and',
        conditions: Array.isArray(group?.conditions) ? group.conditions : [],
        groups: Array.isArray(group?.groups) ? group.groups.map(normalizeGroup) : [],
    };
}
/** Any rule, legacy-flat or multi-branch, as its list of branches. */
export function ruleBranchesOf(rule) {
    if (rule.branches?.length) {
        return rule.branches.map((b) => ({ ...b, when: normalizeGroup(b.when) }));
    }
    /* eslint-disable @typescript-eslint/no-deprecated -- reading the legacy shape is this function's job */
    return [
        {
            id: rule.id,
            when: { combinator: 'and', conditions: rule.conditions ?? [], groups: [] },
            action: rule.action ?? 'require_field',
            targetFieldId: rule.targetFieldId ?? '',
            values: rule.values,
            defaultValue: rule.defaultValue,
        },
    ];
    /* eslint-enable @typescript-eslint/no-deprecated */
}
function cellValue(cells, fieldId) {
    const raw = cells[fieldId];
    if (Array.isArray(raw)) {
        return raw[0] ?? '';
    }
    return raw ?? '';
}
/**
 * `and` needs every leaf and nested group, `or` any. An empty group holds: that
 * is what the schema editor writes for an unconditional branch.
 */
export function conditionGroupHolds(group, cells) {
    const leaves = group.conditions.map((condition) => {
        const actual = cellValue(cells, condition.fieldId);
        return condition.operator === 'is_not'
            ? actual !== condition.value
            : actual === condition.value;
    });
    const all = [...leaves, ...group.groups.map((g) => conditionGroupHolds(g, cells))];
    if (all.length === 0) {
        return true;
    }
    return group.combinator === 'or' ? all.some(Boolean) : all.every(Boolean);
}
/**
 * Fields a `show_field` rule hides for this record. A targeted field is hidden
 * unless SOME branch targeting it fires, so the decision waits for every branch;
 * deciding per branch let a non-firing one hide what a later one reveals.
 */
export function hiddenFieldIds(fields, rules, cells) {
    const known = new Set(fields.map((f) => f.id));
    const shown = new Map();
    for (const rule of rules) {
        for (const branch of ruleBranchesOf(rule)) {
            if (branch.action !== 'show_field' || !known.has(branch.targetFieldId)) {
                continue;
            }
            const fires = conditionGroupHolds(branch.when, cells);
            shown.set(branch.targetFieldId, (shown.get(branch.targetFieldId) ?? false) || fires);
        }
    }
    return new Set([...shown].filter(([, isShown]) => !isShown).map(([id]) => id));
}
/**
 * Fields this record must fill: the ones marked required, and the targets of every
 * `require_field` branch that fires for it. Whether a hidden one still counts is the caller's
 * call; the grid lets it go, since nobody can fill a cell they cannot see.
 */
export function requiredFieldIds(fields, rules, cells) {
    const required = new Set(fields.filter((f) => f.required).map((f) => f.id));
    const known = new Set(fields.map((f) => f.id));
    for (const rule of rules) {
        for (const branch of ruleBranchesOf(rule)) {
            if (branch.action === 'require_field' &&
                known.has(branch.targetFieldId) &&
                conditionGroupHolds(branch.when, cells)) {
                required.add(branch.targetFieldId);
            }
        }
    }
    return required;
}
/**
 * The vocabulary a field draws from: its resolved list when it is bound, its own values otherwise.
 * A bound field's `values` is a stale private copy, so the list wins. An empty resolution means
 * the list has not loaded, never an empty list.
 */
export function vocabularyOf(field, resolved) {
    return field.listRef?.listId && resolved && resolved.length > 0 ? resolved : field.values;
}
/**
 * A vocabulary with the entries a cell cannot hold removed: blanks, and repeats of a value
 * (the first spelling wins).
 *
 * Neither the schema builder nor a shared list stops either from being published, and both
 * break a picker: two blank options share a key, and an empty value is what clears a cell.
 * Deduped on the value, because the stored cell is drawn from it.
 */
export function usableOptions(values) {
    const seen = new Set();
    return values.filter((option) => {
        const value = option.value.trim();
        if (!value || seen.has(value)) {
            return false;
        }
        seen.add(value);
        return true;
    });
}
/**
 * The options one row may pick in a field: its usable vocabulary, narrowed by every
 * `limit_options` branch that fires for the row's cells. The grid's picker and the server's
 * checks both read this, so they never disagree on what a cell may hold.
 *
 * Narrowing INTERSECTS rather than replaces, so two firing branches narrow instead of the
 * second undoing the first, whatever order they were authored in.
 */
export function rowOptions(vocabulary, fieldId, rules, cells) {
    let options = usableOptions(vocabulary);
    for (const rule of rules) {
        for (const branch of ruleBranchesOf(rule)) {
            if (branch.action === 'limit_options' &&
                branch.targetFieldId === fieldId &&
                conditionGroupHolds(branch.when, cells)) {
                const allowed = new Set(branch.values ?? []);
                options = options.filter((option) => allowed.has(option.value));
            }
        }
    }
    return options;
}
/**
 * The primary output first, then the rest. With none authored (every schema the
 * product creates) a single `null` stands for the default concatenation.
 */
export function orderedOutputs(outputs) {
    if (outputs.length === 0) {
        return [null];
    }
    return [...outputs.filter((o) => o.isPrimary), ...outputs.filter((o) => !o.isPrimary)];
}
export function assembleRecord(input, cells) {
    const options = {
        fieldDelimiter: input.fieldDelimiter ?? undefined,
        subfieldDelimiter: input.subfieldDelimiter ?? undefined,
        resolvedValues: input.resolvedValues,
        now: input.now,
        hiddenFieldIds: hiddenFieldIds(input.fields, input.rules, cells),
    };
    const outputs = {};
    let assembledName = null;
    orderedOutputs(input.outputs).forEach((output, i) => {
        const value = assembleOutput(output, input.fields, cells, options);
        if (output) {
            outputs[output.id] = value;
        }
        if (i === 0) {
            assembledName = value || null;
        }
    });
    return { assembledName, outputs };
}
